import { ProfileStatusRules } from './rules/ProfileStatusRules';
import { UserProfile, BusinessRuleResult, ConnectionData, TestMethod, ProfileStatus } from './types';
import { Profile, ProfileData } from '@/types/profile';
import { PlanRules } from './rules/PlanRules';
import { UserInteractionRules } from './rules/UserInteractionRules';
import { VisibilityRules } from './rules/VisibilityRules';
import { PermissionRules } from './rules/PermissionRules';
import { ValidationRules } from './rules/ValidationRules';

export class BusinessRulesEngine {
  private static instance: BusinessRulesEngine;

  static getInstance(): BusinessRulesEngine {
    if (!BusinessRulesEngine.instance) {
      BusinessRulesEngine.instance = new BusinessRulesEngine();
    }
    return BusinessRulesEngine.instance;
  }

  // Plan Rules (agora async)
  async canAccessFeature(user: UserProfile, feature: string): Promise<BusinessRuleResult> {
    return PlanRules.canAccessFeature(user, feature);
  }

  getPlanLimitations(planType: string) {
    return PlanRules.getPlanLimitations(planType);
  }

  isPlanExpired(user: UserProfile): boolean {
    return PlanRules.isPlanExpired(user);
  }

  getEffectivePlan(user: UserProfile): string {
    return PlanRules.getEffectivePlan(user);
  }

  // User Interaction Rules (agora async)
  async canInteractWith(sourceUser: UserProfile, targetUser: UserProfile): Promise<BusinessRuleResult> {
    return UserInteractionRules.canInteractWith(sourceUser, targetUser);
  }

  async canSendMessage(sourceUser: UserProfile, targetUser: UserProfile): Promise<BusinessRuleResult> {
    return UserInteractionRules.canSendMessage(sourceUser, targetUser);
  }

  async canViewProfile(viewerUser: UserProfile, profileUser: UserProfile): Promise<BusinessRuleResult> {
    return UserInteractionRules.canViewProfile(viewerUser, profileUser);
  }

  // Visibility Rules (agora async)
  async shouldShowProfile(viewerUser: UserProfile, profileUser: UserProfile): Promise<BusinessRuleResult> {
    return VisibilityRules.shouldShowProfile(viewerUser, profileUser);
  }

  async filterConnections(currentUser: UserProfile, connections: ConnectionData[]): Promise<BusinessRuleResult<ConnectionData[]>> {
    return VisibilityRules.filterConnections(currentUser, connections);
  }

  async shouldShowInFeed(viewerUser: UserProfile, profileUser: UserProfile): Promise<BusinessRuleResult> {
    return VisibilityRules.shouldShowInFeed(viewerUser, profileUser);
  }

  // Permission Rules (agora async)
  async hasPermission(user: UserProfile, action: string): Promise<BusinessRuleResult> {
    return PermissionRules.hasPermission(user, action);
  }

  async canAccessAdminPanel(user: UserProfile): Promise<BusinessRuleResult> {
    return PermissionRules.canAccessAdminPanel(user);
  }

  async canManageUsers(user: UserProfile): Promise<BusinessRuleResult> {
    return PermissionRules.canManageUsers(user);
  }

  async canAccessFeatureFlag(user: UserProfile, flagName: string): Promise<BusinessRuleResult> {
    return PermissionRules.canAccessFeatureFlag(user, flagName);
  }

  // Validation Rules (agora async)
  validateProfileCompleteness(user: UserProfile): BusinessRuleResult {
    return ValidationRules.validateProfileCompleteness(user);
  }

  validatePlanChange(user: UserProfile, newPlan: string): BusinessRuleResult {
    return ValidationRules.validatePlanChange(user, newPlan);
  }

  async validateInteractionLimits(user: UserProfile, action: string, currentCount: number): Promise<BusinessRuleResult> {
    return ValidationRules.validateInteractionLimits(user, action, currentCount);
  }

  async canViewConnections(user: UserProfile): Promise<BusinessRuleResult> {
    return ValidationRules.canViewConnections(user);
  }

  async canViewNotificationDetails(user: UserProfile, notificationType: string): Promise<BusinessRuleResult> {
    return ValidationRules.canViewNotificationDetails(user, notificationType);
  }

  validateMinimumPhotos(user: { profile_photos?: string[] }): BusinessRuleResult {
    return ValidationRules.validateMinimumPhotos(user);
  }

  validateProfileComplete(profile: any): BusinessRuleResult {
    return ValidationRules.validateProfileComplete(profile);
  }

  validateProfileVerified(profile: any): BusinessRuleResult {
    return ValidationRules.validateProfileVerified(profile);
  }

  // Chat Access Rules
  canAccessChat(profile: any): BusinessRuleResult {
    return ValidationRules.canAccessChat(profile);
  }

  // Profile Status Rules
  async getProfileStatus(profile: Profile | ProfileData): Promise<ProfileStatus> {
    return await ProfileStatusRules.getProfileStatus(profile);
  }

  // Método para obter todos os métodos testáveis
  getTestMethods(): TestMethod[] {
    return [
      {
        name: 'canAccessFeature',
        description: 'Verifica se um usuário pode acessar uma funcionalidade específica',
        category: 'plan',
        parameters: [
          { name: 'user', type: 'object', required: true, description: 'Objeto UserProfile' },
          { name: 'feature', type: 'string', required: true, description: 'Nome da funcionalidade' }
        ]
      },
      {
        name: 'canInteractWith',
        description: 'Verifica se um usuário pode interagir com outro usuário',
        category: 'interaction',
        parameters: [
          { name: 'sourceUser', type: 'object', required: true, description: 'Usuário que inicia a interação' },
          { name: 'targetUser', type: 'object', required: true, description: 'Usuário alvo da interação' }
        ]
      },
      {
        name: 'shouldShowProfile',
        description: 'Verifica se um perfil deve ser mostrado para um usuário',
        category: 'visibility',
        parameters: [
          { name: 'viewerUser', type: 'object', required: true, description: 'Usuário que está visualizando' },
          { name: 'profileUser', type: 'object', required: true, description: 'Usuário do perfil sendo visualizado' }
        ]
      },
      {
        name: 'hasPermission',
        description: 'Verifica se um usuário tem uma permissão específica',
        category: 'permission',
        parameters: [
          { name: 'user', type: 'object', required: true, description: 'Objeto UserProfile' },
          { name: 'action', type: 'string', required: true, description: 'Ação/permissão a verificar' }
        ]
      },
      {
        name: 'validateInteractionLimits',
        description: 'Valida se um usuário pode realizar uma ação baseado em limites',
        category: 'validation',
        parameters: [
          { name: 'user', type: 'object', required: true, description: 'Objeto UserProfile' },
          { name: 'action', type: 'string', required: true, description: 'Ação a ser validada' },
          { name: 'currentCount', type: 'number', required: true, description: 'Contador atual da ação' }
        ]
      },
      {
        name: 'canViewConnections',
        description: 'Verifica se um usuário pode visualizar conexões sem restrições',
        category: 'validation',
        parameters: [
          { name: 'user', type: 'object', required: true, description: 'Objeto UserProfile' }
        ]
      },
      {
        name: 'canViewNotificationDetails',
        description: 'Verifica se um usuário pode ver detalhes completos de uma notificação',
        category: 'validation',
        parameters: [
          { name: 'user', type: 'object', required: true, description: 'Objeto UserProfile' },
          { name: 'notificationType', type: 'string', required: true, description: 'Tipo da notificação' }
        ]
      },
      {
        name: 'validateProfileComplete',
        description: 'Verifica se todas as seções do perfil estão completas',
        category: 'validation',
        parameters: [
          { name: 'profile', type: 'object', required: true, description: 'Dados do perfil do usuário' }
        ]
      },
      {
        name: 'validateProfileVerified',
        description: 'Verifica se o usuário está completamente verificado',
        category: 'validation',
        parameters: [
          { name: 'profile', type: 'object', required: true, description: 'Dados do perfil do usuário' }
        ]
      }
    ];
  }
}

// Instância singleton para uso global
export const businessRulesEngine = BusinessRulesEngine.getInstance();
